<script setup>
import { computed, ref } from "vue";
import Vapor from "laravel-vapor";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import BrandThrobber from "@/Components/Brand/BrandThrobber.vue";
import ExpertListCover from "@/Components/Profile/ExpertListCover.vue";
import { useMastheadImageUrls } from "@/composables/useMastheadImageUrls.js";

function asset(path) {
    return Vapor.asset(path);
}

const props = defineProps({
    creator: {
        type: String,
        default: "Molly's World",
    },
    title: {
        type: String,
        default: "Top Gifts for Kids: 2026",
    },
    description: {
        type: String,
        default: "",
    },
    image: {
        type: String,
        default: "",
    },
    avatar: {
        type: String,
        default: "",
    },
    href: {
        type: String,
        default: "#",
    },
    // Dashboard cards sit on a white panel and need a visible edge; the
    // homepage cards (on a purple panel) don't.
    bordered: {
        type: Boolean,
        default: false,
    },
    // Filled chip state for lists the current user has bookmarked.
    bookmarked: {
        type: Boolean,
        default: false,
    },
    // Bookmark request in flight (GH-994): the chip swaps its icon for the
    // throbber dots and ignores clicks.
    busy: {
        type: Boolean,
        default: false,
    },
    // A Gift Hero's expert list (GH-1299): the cover shows the list's header
    // photo when it has one, otherwise the editorial art (purple field +
    // slab with the item photo inset). Callers pass it only for lists owned
    // by Gift Heroes.
    expert: {
        type: Boolean,
        default: false,
    },
    // The list's masthead photo (Cloudinary public id) and focal point.
    mastheadPhoto: {
        type: String,
        default: null,
    },
    mastheadFocusX: {
        type: Number,
        default: null,
    },
    mastheadFocusY: {
        type: Number,
        default: null,
    },
});

defineEmits(["bookmark"]);

// Cover and title link through to the list; the homepage sample cards have
// no destination (href defaults to "#"), so they stay static there.
const hasLink = computed(() => Boolean(props.href) && props.href !== "#");

// Reveal the placeholder background if the avatar/cover image fails to load.
const avatarFailed = ref(false);
const imageFailed = ref(false);

// Cover-less lists arrive as Cloudinary's blank "missing-image" fallback,
// not an empty string — treat that as no cover too (GH-1062).
const hasCover = computed(
    () =>
        props.image &&
        !props.image.includes("missing-image") &&
        !imageFailed.value,
);

// Header photo for expert covers, on the owner's focal point (BH-38); a
// 404 falls through to the editorial art.
const headerFailed = ref(false);
const headerImage = computed(() => {
    if (!props.expert || !props.mastheadPhoto || headerFailed.value) {
        return null;
    }

    const focus =
        Number.isFinite(props.mastheadFocusX) &&
        Number.isFinite(props.mastheadFocusY)
            ? { x: props.mastheadFocusX, y: props.mastheadFocusY }
            : null;

    return useMastheadImageUrls(props.mastheadPhoto, focus);
});
</script>

<template>
    <article
        class="flex h-full w-full flex-col overflow-hidden rounded-brand bg-white"
        :class="bordered ? 'border border-solid border-brand-tan' : ''"
    >
        <div class="relative">
            <component
                :is="hasLink ? 'a' : 'div'"
                :href="hasLink ? href : null"
                :aria-label="hasLink ? 'View ' + title : null"
                class="grid aspect-[271/186] w-full place-items-center overflow-hidden bg-brand-tan/40"
                data-test="expert-card-cover"
            >
                <img
                    v-if="headerImage"
                    :src="headerImage.card2xSrc"
                    :alt="title"
                    class="size-full object-cover"
                    :style="{ objectPosition: headerImage.position }"
                    data-test="expert-card-header-photo"
                    @error="headerFailed = true"
                />
                <ExpertListCover
                    v-else-if="expert"
                    :image="hasCover ? image : null"
                    layout="card"
                />
                <img
                    v-else-if="hasCover"
                    :src="image"
                    :alt="title"
                    class="size-full object-cover"
                    @error="imageFailed = true"
                />
                <!-- Branded placeholder instead of a blank block when the
                     list has no cover (GH-1062). -->
                <img
                    v-else
                    :src="asset('images/brand/logo-mark.png')"
                    alt=""
                    aria-hidden="true"
                    class="size-14 opacity-25 grayscale"
                />
            </component>
            <button
                type="button"
                :aria-label="
                    bookmarked
                        ? `Remove ${title} from Saved Lists`
                        : `Save ${title}`
                "
                :aria-pressed="bookmarked"
                class="group absolute right-3 top-3 grid size-[30px] place-items-center rounded-[2px] shadow-[0_2px_6px_rgba(0,0,0,0.28)] ring-1 ring-black/10 backdrop-blur-sm transition hover:bg-brand-navy/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-navy"
                :class="[
                    bookmarked ? 'bg-brand-navy/90' : 'bg-white/30',
                    busy ? 'pointer-events-none' : '',
                ]"
                :aria-busy="busy || undefined"
                :disabled="busy"
                @click="$emit('bookmark')"
            >
                <BrandThrobber
                    v-if="busy"
                    variant="mono"
                    class="text-[10px]"
                    :class="bookmarked ? 'text-white' : 'text-brand-navy'"
                />
                <svg
                    v-else
                    aria-hidden="true"
                    viewBox="0 0 13 14.9621"
                    fill="currentColor"
                    class="h-[15px] w-[13px] transition-colors group-hover:text-white"
                    :class="bookmarked ? 'text-white' : 'text-brand-navy'"
                >
                    <path
                        d="M0 14.9621V1.66246C0 1.20528 0.15925 0.814051 0.47775 0.488763C0.79625 0.163475 1.17867 0.000554153 1.625 0H6.5V1.66246H1.625V12.4269L5.6875 10.6397L9.75 12.4269V6.64984H11.375V14.9621L5.6875 12.4684L0 14.9621ZM9.75 4.98738V3.32492H8.125V1.66246H9.75V0H11.375V1.66246H13V3.32492H11.375V4.98738H9.75Z"
                    />
                </svg>
            </button>
        </div>

        <div class="flex flex-1 flex-col px-[17px] pb-[17px] pt-4">
            <!-- Creator -->
            <div class="flex items-center gap-2">
                <span
                    class="size-[25px] shrink-0 overflow-hidden rounded-[2px] bg-brand-tan/60"
                >
                    <img
                        v-if="avatar && !avatarFailed"
                        :src="avatar"
                        :alt="creator"
                        class="size-full rounded-[2px] object-cover"
                        @error="avatarFailed = true"
                    />
                </span>
                <span class="font-brand text-[14px] font-medium text-[#102a3d]">
                    {{ creator }}
                </span>
            </div>

            <!-- Title -->
            <h3
                class="mt-[18px] font-brand text-[22px] font-semibold leading-[1.2] text-[#102a3d]"
            >
                <component
                    :is="hasLink ? 'a' : 'span'"
                    :href="hasLink ? href : null"
                    class="text-[#102a3d] transition-colors hover:text-brand-red"
                    data-test="expert-card-title"
                >
                    {{ title }}
                </component>
            </h3>

            <!-- Description grows to absorb the height difference between
                 cards (min height reserved), so every CTA sits on the same
                 baseline across a row/carousel (GH-1064). -->
            <p
                v-if="description"
                class="mt-2.5 min-h-[55px] flex-1 font-brand text-[14px] leading-[1.3] text-[#565a77]"
            >
                {{ description }}
            </p>
            <div v-else class="min-h-[55px] flex-1" aria-hidden="true"></div>

            <BrandButton :href="href" class="mt-3 px-6 py-3 text-[15px]">
                View Expert List
            </BrandButton>
        </div>
    </article>
</template>
